import type { Device } from "verdi-common/schemas/device/device";
import type { BlockValveOptions } from "../Models/Devices/BlockValve.js";
import { SessionHandler } from "./SessionHandler.js";

export class DeviceAPI {
    /**
     * Creates a device based on the provided parameters
     */
    static async addOne(options: BlockValveOptions): Promise<Record<string, unknown>> {
        const url = "device/create";

        const response = await SessionHandler.doFetch(url, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify(options),
        }).catch((e) => {
            throw new Error(e.message || "Unknown error");
        });
        return await response.json();
    }

    /**
     * Delete a single device based on it's ID
     * @param devId ID of device to delete
     */
    static async deleteOne(devId: string): Promise<boolean> {
        await SessionHandler.doFetch("devices/" + devId, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "DELETE",
            redirect: "follow",
        });
        return true;
    }

    /**
     * Gets the information for a single device
     * @param query an enclosureID, deviceID, or PCB QR code corresponding to query.
     */
    static async getOne(query: string): Promise<Device> {
        const response = await SessionHandler.doFetch("devices/" + query, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "GET",
            redirect: "follow",
        });
        return await response.json();
    }

    /**
     * Gets the information for a single device based on a scan
     * admin only
     * @param query an enclosureID, deviceID, or PCB QR code corresponding to query.
     */
    static async debuggingDeviceQuery(query: string): Promise<{
        NodeId: string;
        QRs: { Enclosure: string; PCB: string };
        DeviceDoc: Record<string, unknown>;
        MicrovalveDoc: Record<string, unknown>;
    }> {
        const response = await SessionHandler.doFetch("devices/info/" + query, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "GET",
            redirect: "follow",
        });
        return await response.json();
    }

    /**
     * Gets the information for a single device
     */
    static async editOne(id: string, args): Promise<Device> {
        const response = await SessionHandler.doFetch("device/edit/" + id, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify({
                ...args,
            }),
        });
        return await response.json();
    }

    /**
     * Starts a network test on the requested device.
     */
    static async conductNetworkTestOnOne(id: string, args = {}): Promise<Device> {
        const response = await SessionHandler.doFetch("device/networkTest/" + id, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify({
                ...args,
            }),
        });
        return await response.json();
    }

    /**
     * Sets the manual mode on a device
     */
    static async setRemoteManualModeOnOne(id: string, mode, args = {}): Promise<Device> {
        const response = await SessionHandler.doFetch(`device/setManualOverride/${id}/${mode}`, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify({
                ...args,
            }),
        });
        return await response.json();
    }

    /**
     * Gets the information for a single device
     */
    static async clearWarnings(id: string): Promise<Device> {
        const response = await SessionHandler.doFetch("device/clearWarnings/" + id, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify({}),
        });
        return await response.json();
    }
    /**
     * Gets the information for a single device
     */
    static async clearVerification(id: string): Promise<Device> {
        const response = await SessionHandler.doFetch("device/clearVerification/" + id, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            method: "POST",
            redirect: "follow",
            body: JSON.stringify({}),
        });
        return await response.json();
    }
}
